import { Model, useRepo } from 'pinia-orm';
import { Attr, BelongsTo, HasMany, HasManyThrough, HasOne, Num, Str } from 'pinia-orm/dist/decorators';
import Claim from './Claim';
import LumpSumCost from './LumpSumCost';
import Package from './Package';
import RatesCost from './RatesCost';
import Trade from './Trade';
import TradeItem from './TradeItem';
import WbsCode from './WbsCode';
/* --- user header --- */
/* --- end user header --- */

export default class PackageTradeItem extends Model {
  static entity = 'App\\Models\\PackageTradeItem';
  // fields
  @Num(0)
  declare id: number;
  @Num(0)
  declare package_id: number;
  @Num(0)
  declare trade_item_id: number;
  @Num(0)
  declare sort_order: number;
  @Num(undefined)
  declare wbs_code_id: number;
  @Attr(undefined)
  declare quantity: number | undefined;
  @Attr(undefined)
  declare rate: number | undefined;
  @Num(0)
  declare value: number;
  @Num(undefined)
  declare baseline_forecast_amount: number | undefined;
  @Attr(undefined)
  declare actual_forecast_amount: number[] | undefined;
  @Num(undefined)
  declare awarded_works: number | undefined;
  @Str('')
  declare created_at: string | undefined;
  @Str('')
  declare updated_at: string | undefined;
  // relations
  @BelongsTo(() => Package, 'package_id', 'id')
  declare package: Package;
  @BelongsTo(() => TradeItem, 'trade_item_id', 'id')
  declare trade_item: TradeItem;
  @BelongsTo(() => WbsCode, 'wbs_code_id', 'id')
  declare wbs_code: WbsCode;
  @HasMany(() => RatesCost, 'package_trade_item_id', 'id')
  declare rates_costs: RatesCost[];
  @HasMany(() => LumpSumCost, 'package_trade_item_id', 'id')
  declare lump_sum_costs: LumpSumCost[];
  // NOTE pinia orm doesn't have HasOneThrough, so this will do for now...
  @HasManyThrough(() => Trade, () => TradeItem, 'id', 'id', 'trade_item_id', 'trade_id')
  declare trade: Trade[];
  /* --- user code --- */
  @Attr(undefined)
  declare $previous: number | undefined;

  public previous(claim: Claim) {
    if(this.$previous !== undefined) {
      return this.$previous;
    }

    let totalPrevious = 0;

    if(claim.boq_type === 'subcontractor_lump_sum') {
      const previousCosts = useRepo(LumpSumCost)
        .where('package_trade_item_id', this.id)
        .whereHas('claim', (query) => {
          query.where('id', (id) => {
            return id !== claim.id;
          })
            .where('end', (end) => {
              return end < claim.end;
            });
        }).get();

      previousCosts.forEach((cost) => {
        totalPrevious += cost.current_certified;
      });
    } else if(claim.boq_type === 'subcontract_schedule_of_rates') {
      const previousCosts = useRepo(RatesCost)
        .where('trade_item_id', this.id)
        .whereHas('claim', (query) => {
          query.where('id', (id) => {
            return id !== claim.id;
          })
            .where('end', (end) => {
              return end < claim.end;
            });
        }).get();

      previousCosts.forEach((cost) => {
        totalPrevious += cost.current_certified;
      });
    }

    return totalPrevious;
  }

  get billItem() {
    return this.sort_order;
  }

  public actualForecastForClaim(claim: Claim) {
    const claimIndex = (claim.claim_number ?? 1) - 1;
    const forecastAmounts = this.actual_forecast_amount || [];

    return forecastAmounts[claimIndex] ?? 0;
  }

  public previousActualForecastForClaim(claim: Claim) {
    const claimIndex = (claim.claim_number ?? 1) - 1;
    const forecastAmounts = this.actual_forecast_amount || [];

    return forecastAmounts[claimIndex - 1] ?? 0;
  }

  /* --- end user code --- */
}
